import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';
import { IRole } from '../../../../types/settingsDataModule';
import { IInstitutionUser } from '../../../../pages/organization/settings/types/SettingsModuleTypes';
import { SettingsService } from '../../../../services/settings/settings.service';
import { failureReason } from '../../../../../utils/http-errors';
import {
  describeRole,
  formatRoleName,
  pickableRoles,
} from '../../../../pages/organization/settings/settings-roles';

let timeout: any;

/** Change someone's name, number and roles. Locked roles cannot be taken away here. */
@Component({
  selector: 'app-edit-user-form',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    InputFieldComponent,
    NgxMatInputTelComponent,
    ReactiveFormsModule,
    CustomizableButtonComponent,
  ],
  templateUrl: './edit-user-form.component.html',
  styleUrls: ['../../../../pages/organization/settings/settings-ui.css'],
})
export class EditUserFormComponent implements OnInit, OnChanges, OnDestroy {
  constructor(private builder: FormBuilder) {}
  settingsService = inject(SettingsService);
  private destroyRef = inject(DestroyRef);

  @Output() onClose = new EventEmitter();
  @Output() onSuccess = new EventEmitter<void>();
  @Input({ required: true }) openModal = false;
  @Input({ required: true }) user: IInstitutionUser | null = null;
  /** True when the person being edited is the one signed in. */
  @Input() isSelf = false;
  /** Role name → why it cannot be removed (your own access, the only administrator). */
  @Input() lockedRoles: Record<string, string> = {};

  loading = false;
  errorMsg = '';
  submitted = false;

  showSelectUserRoleModal = false;
  selectedRoles: IRole[] = [];
  private allRoles: IRole[] = [];
  /** Roles offered in the list, most senior first. */
  roleOptions: IRole[] = [];

  editForm = this.builder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    emailAddress: ['', [Validators.required, Validators.email]],
    primaryContactNumber: ['', Validators.required],
    roles: [[] as string[], Validators.required],
  });

  get fullName(): string {
    return `${this.user?.firstName || ''} ${this.user?.lastName || ''}`.trim();
  }

  ngOnInit() {
    this.settingsService.fetchUserRoles();
    this.settingsService.rolesData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((roles: IRole[]) => {
      this.allRoles = roles || [];
      this.roleOptions = pickableRoles(this.allRoles);
      this.syncSelectedRolesFromUser();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['user'] && this.user) {
      this.submitted = false;
      this.errorMsg = '';
      this.editForm.patchValue({
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        emailAddress: this.user.emailAddress || this.user.email,
        // Keep the number on record so a name change does not need it typed again.
        primaryContactNumber: this.user.attributes?.phoneNumber?.[0] || '',
      });
      this.syncSelectedRolesFromUser();
      this.editForm.markAsPristine();
    }
  }

  private syncSelectedRolesFromUser() {
    if (!this.user || !this.allRoles.length) return;
    this.selectedRoles = this.allRoles.filter((role) => this.user!.realmRoles?.includes(role.name));
    this.editForm.patchValue({ roles: this.selectedRoles.map((r) => r.name) });
  }

  describe(role: IRole): string {
    return describeRole(role.name, this.allRoles);
  }

  label(role: IRole): string {
    return formatRoleName(role.name);
  }

  isSelected(role: IRole): boolean {
    return this.selectedRoles.some((r) => r.name === role.name);
  }

  lockReason(role: IRole): string {
    return this.isSelected(role) ? this.lockedRoles?.[role.name] || '' : '';
  }

  /** Ticks or unticks a role in the list. Locked roles stay ticked. */
  toggleRole(role: IRole, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (!checked && this.lockReason(role)) {
      (event.target as HTMLInputElement).checked = true;
      return;
    }
    const next = checked
      ? [...this.selectedRoles.filter((r) => r.name !== role.name), role]
      : this.selectedRoles.filter((r) => r.name !== role.name);
    this.updateSelectedRole(next);
    this.editForm.markAsDirty();
  }

  openSelectUserRoleModal() {
    this.showSelectUserRoleModal = true;
  }

  closeSelectUserRoleModal() {
    this.showSelectUserRoleModal = false;
  }

  updateSelectedRole(roles: IRole[]) {
    this.selectedRoles = roles;
    this.editForm.patchValue({ roles: this.selectedRoles.map((r) => r.name) });
  }

  removeRole(roleId: string) {
    const role = this.selectedRoles.find((r) => r.id === roleId);
    if (role && this.lockReason(role)) return;
    this.selectedRoles = this.selectedRoles.filter((r) => r.id !== roleId);
    this.editForm.patchValue({ roles: this.selectedRoles.map((r) => r.name) });
  }

  getFormInput(name: keyof typeof this.editForm.controls) {
    return this.editForm.get(name);
  }

  get emailErrorMsg() {
    const email = this.getFormInput('emailAddress');
    if (email?.errors?.['email']) return 'Enter a full email address, like name@company.com.';
    if (email?.errors?.['required']) return 'Enter their email address.';
    return '';
  }

  handleSubmit() {
    this.submitted = true;
    if (!this.editForm.valid || !this.user) return;

    // Belt and braces: a locked role is never sent without itself.
    const roles = [...(this.editForm.value.roles || [])];
    for (const locked of Object.keys(this.lockedRoles || {})) {
      if (this.user.realmRoles?.includes(locked) && !roles.includes(locked)) roles.push(locked);
    }

    this.loading = true;
    this.settingsService
      .updateUserProfile(this.user.id, {
        firstName: this.editForm.value.firstName!,
        lastName: this.editForm.value.lastName!,
        emailAddress: this.editForm.value.emailAddress!,
        phoneNumber: this.editForm.value.primaryContactNumber!,
        authorities: roles,
      })
      .subscribe({
        next: () => {
          this.loading = false;
          // Refresh the list, keeping its search, filter and page
          this.settingsService.refreshInstitutionUsers();
          this.onSuccess.emit();
          this.closeModal();
        },
        error: (error) => {
          this.loading = false;
          this.errorMsg = `The changes were not saved. ${failureReason(
            error,
            'That account no longer exists. Close this and refresh the list.'
          )}`;
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            this.errorMsg = '';
          }, 8000);
        },
      });
  }

  closeModal() {
    this.onClose.emit();
  }

  ngOnDestroy() {
    if (timeout) clearTimeout(timeout);
  }
}
